import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { Badge } from '@/components/shared/Badge';
import { GlassCard } from '@/components/shared/GlassCard';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { Button } from '@/components/ui/button';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { fadeUp, inViewOnce, staggerContainer } from '@/lib/motion/variants';
import { formatRupees } from '@/lib/utils/format';
import { useListings } from '@/features/marketplace/hooks/useListings';
import type { Listing } from '@/features/marketplace/types';

function imgSrc(listing: Listing): string {
  return listing.media_url ?? unsplash(photo.guideM1, { w: 700 });
}

export function GuidesGrid() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data: guides = [], isLoading } = useListings({ type: 'guide', per_page: 6 });

  function scrollBy(dir: 'left' | 'right') {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.7;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  }

  return (
    <section className="mx-auto mt-24 max-w-7xl px-4 md:px-6">
      <SectionHeader
        kicker="The Index · 38 vetted guides"
        title={
          <>
            Locals who <em>grew up on the mountain.</em>
          </>
        }
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="Scroll left"
              onClick={() => scrollBy('left')}
              className="h-9 w-9 rounded-full p-0"
            >
              <ArrowLeft size={14} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Scroll right"
              onClick={() => scrollBy('right')}
              className="h-9 w-9 rounded-full p-0"
            >
              <ArrowRight size={14} />
            </Button>
            <Button asChild variant="outline" size="sm" className="hidden md:inline-flex">
              <Link to="/guides">
                All guides <ArrowRight size={14} />
              </Link>
            </Button>
          </div>
        }
      />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={inViewOnce}
        variants={staggerContainer}
        ref={scrollRef}
        className="mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => (
              <motion.div key={i} variants={fadeUp} className="snap-start shrink-0 basis-[280px] sm:basis-[320px]">
                <GlassCard className="overflow-hidden p-0">
                  <div className="aspect-[3/4] animate-pulse bg-white/10" />
                  <div className="space-y-2 p-3.5">
                    <div className="h-3 w-1/2 animate-pulse rounded bg-white/10" />
                    <div className="h-5 w-1/3 animate-pulse rounded bg-white/10" />
                  </div>
                </GlassCard>
              </motion.div>
            ))
          : guides.map((g) => (
              <motion.div key={g.id} variants={fadeUp} className="snap-start shrink-0 basis-[280px] sm:basis-[320px]">
                <Link to={`/guides/${g.slug}`} className="block">
                  <GlassCard interactive className="overflow-hidden p-0">
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={imgSrc(g)}
                        alt={g.name}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      <div className="absolute left-3 top-3">
                        <Badge color={brand.teal} icon={Check}>
                          Verified
                        </Badge>
                      </div>
                      <div
                        className="absolute inset-x-0 bottom-0 px-4 pb-4 pt-10"
                        style={{ background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.92))' }}
                      >
                        {g.tags?.[0] && (
                          <div
                            className="text-[10px] font-bold uppercase tracking-[0.15em]"
                            style={{ color: '#8BD8C2' }}
                          >
                            {g.tags[0]}
                          </div>
                        )}
                        <div className="font-display mt-1 text-2xl font-semibold text-white">
                          {g.name}
                        </div>
                        {g.location?.city && (
                          <div className="mt-1 text-[11px] text-white/80">{g.location.city}</div>
                        )}
                        {g.amenities?.[0] && (
                          <p className="mt-2 text-[12px] leading-snug text-white/85">{g.amenities[0]}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3.5">
                      <span className="text-[11px] text-ink-mute">
                        {g.location?.city ?? 'Kashmir'}
                      </span>
                      <span className="text-[13px] font-bold text-ink">
                        {formatRupees(Math.round((g.price_paisa ?? 0) / 100))}
                        <span className="text-[11px] font-normal text-ink-mute">/day</span>
                      </span>
                    </div>
                  </GlassCard>
                </Link>
              </motion.div>
            ))}
      </motion.div>
    </section>
  );
}
